/**
 * Geofence + terms context (#769). Provider: GeoGate.tsx. Outside the provider (unit-test harnesses that render a
 * page or the ticket on its own) everything is allowed and the terms count as accepted.
 */
import { createContext, useContext } from 'react';
import { intlLocale, t } from '../i18n/i18n';
import type { GeoDecision, GeoMode } from './policy';

export interface GeoContextValue {
  /** "loading" until /geo.json answered or timed out. */
  status: 'loading' | 'ready';
  /** The jurisdiction decision (shared/policy/jurisdictions.json), the same on both networks. */
  decision: GeoDecision;
  /**
   * The mode that applies on the current network (policy.effectiveMode, owner decision D1): browse-only applies on
   * mainnet only; blocked applies on both. Banners, the ticket and every trading guard read this (via canTrade).
   */
  mode: GeoMode;
  /** Where the country came from: the CloudFront answer, or "unknown" (policy `unknown` = browse_only). */
  source: 'cloudfront' | 'unknown';
  /** Order ticket, 1-click trading, builder-fee approval and Close are usable. */
  canTrade: boolean;
  /** Push registration is allowed (D6: allowed + browse-only, never blocked). See canRegisterPush(). */
  pushAllowed: boolean;
  /** The current terms version is accepted (the first-run tour waits for this). */
  termsAccepted: boolean;
  acceptTerms: () => void;
  /** Open the terms read-only (footer link). */
  showTerms: () => void;
}

const noop = () => undefined;

export const GEO_ALLOWED: GeoContextValue = {
  status: 'ready',
  decision: { mode: 'allowed', country: null, region: null, matched: 'unknown' },
  mode: 'allowed',
  source: 'unknown',
  canTrade: true,
  pushAllowed: true,
  termsAccepted: true,
  acceptTerms: noop,
  showTerms: noop,
};

export const GeoContext = createContext<GeoContextValue | null>(null);

export function useGeo(): GeoContextValue {
  return useContext(GeoContext) ?? GEO_ALLOWED;
}

/**
 * May this browser register for push notifications (D6)? Web has no push yet; this is the hook a future web push
 * client must call before subscribing. False while the location is still loading and whenever the viewer is blocked.
 */
export function canRegisterPush(geo: Pick<GeoContextValue, 'status' | 'pushAllowed'>): boolean {
  return geo.status === 'ready' && geo.pushAllowed;
}

/** The country's name in the UI language ("United States", "アメリカ合衆国"), falling back to the code. */
export function countryName(code: string | null): string {
  if (!code) return t('geo.place.unknown');
  try {
    const n = new Intl.DisplayNames([intlLocale()], { type: 'region' }).of(code);
    return n && n !== code ? n : code;
  } catch {
    return code;
  }
}

/** "Canada (ON)" when a region rule decided, else the country name. */
export function placeLabel(d: GeoDecision): string {
  const name = countryName(d.country);
  return d.matched.includes('-') && d.region ? t('geo.place.region', { country: name, region: d.region }) : name;
}

/** One line explaining browse-only mode (banner, ticket, wallet menu, refused approvals). */
export function browseOnlyMessage(d: GeoDecision): string {
  if (!d.country) return t('geo.browse_only.unknown');
  return t('geo.browse_only.known', { place: placeLabel(d) });
}
